import React, { useRef, useEffect, useState } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Eraser, Link as LinkIcon } from 'lucide-react';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Type here...',
  readOnly = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Initialize the content only once or when value changes externally (and we're not focused)
  useEffect(() => {
    if (editorRef.current && !isFocused && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value, isFocused]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleFormat = (e: React.MouseEvent, command: string) => {
    e.preventDefault();
    execCommand(command);
  };

  const handleLink = (e: React.MouseEvent) => {
    e.preventDefault();
    const url = window.prompt('Enter link URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  if (readOnly) {
    return (
      <div 
        className="is-readonly rtf-content"
        style={{ 
          padding: '10px 12px', 
          border: '1px solid #e2e8f0', 
          borderRadius: '10px', 
          minHeight: '110px',
          background: '#f8fafc',
          color: '#0f172a',
          fontSize: '0.85rem',
          whiteSpace: 'pre-wrap'
        }}
        dangerouslySetInnerHTML={{ __html: value || 'No description provided.' }}
      />
    );
  }

  return (
    <div className={`rtf-container ${isFocused ? 'focused' : ''}`}>
      <div className="rtf-toolbar">
        <button type="button" onMouseDown={(e) => handleFormat(e, 'bold')} title="Bold">
          <Bold size={16} />
        </button>
        <button type="button" onMouseDown={(e) => handleFormat(e, 'italic')} title="Italic">
          <Italic size={16} />
        </button>
        <button type="button" onMouseDown={(e) => handleFormat(e, 'underline')} title="Underline">
          <Underline size={16} />
        </button>
        <div className="rtf-divider" />
        <button type="button" onMouseDown={(e) => handleFormat(e, 'insertUnorderedList')} title="Bullet List">
          <List size={16} />
        </button>
        <button type="button" onMouseDown={(e) => handleFormat(e, 'insertOrderedList')} title="Numbered List">
          <ListOrdered size={16} />
        </button>
        <div className="rtf-divider" />
        <button type="button" onMouseDown={handleLink} title="Add Link">
          <LinkIcon size={16} />
        </button>
        <button type="button" onMouseDown={(e) => handleFormat(e, 'removeFormat')} title="Clear Formatting">
          <Eraser size={16} />
        </button>
      </div>
      
      <div
        ref={editorRef}
        className="rtf-editor"
        contentEditable={!readOnly}
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-placeholder={placeholder}
      />
    </div>
  );
};
